import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { publicAPI } from '../../lib/api';
import { BookOpen, Calendar, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Breadcrumb, breadcrumbJsonLd } from '../../components/Breadcrumb';

export default function DirectoryCategoryPage() {
  const { categorySlug } = useParams();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    publicAPI.getDirectoryCategory(categorySlug, page)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [categorySlug, page]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(221,83%,22%)]"></div></div>;
  if (!data) return <div className="min-h-screen flex flex-col items-center justify-center"><h1 className="text-2xl font-semibold mb-4">Kategori bulunamadi</h1><Link to="/sanayi-rehberi" className="text-[hsl(221,83%,22%)] underline">Rehbere don</Link></div>;

  const { category, articles = [], total, pages } = data;

  const dirCatBreadcrumb = [
    { label: 'Ana Sayfa', href: '/' },
    { label: 'Sanayi Rehberi', href: '/sanayi-rehberi' },
    { label: category.name, href: `/sanayi-rehberi/${categorySlug}` }
  ];

  return (
    <>
      <Helmet>
        <title>{category.name ? `${category.name} Rehberi | Sanayi Rehberi` : 'Sanayi Rehberi'}</title>
        <meta name="description" content={category.description || `${category.name} konusunda rehber makaleler.`} />
        <script type="application/ld+json">{breadcrumbJsonLd(dirCatBreadcrumb)}</script>
      </Helmet>

      <section className="bg-[hsl(221,83%,22%)] text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumb items={dirCatBreadcrumb} variant="light" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold" data-testid="dir-cat-title">{category.name} Rehberi</h1>
          <p className="mt-2 text-blue-100">{category.description}</p>
          {total > 0 && <p className="mt-2 text-sm text-blue-200">{total} makale</p>}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {articles.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">Bu kategoride henuz makale yok.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map(article => (
                <Link key={article.id} to={`/blog/${article.slug}`}>
                  <Card className="h-full hover:shadow-md transition-shadow group" data-testid={`dir-article-${article.slug}`}>
                    {article.cover_image && (
                      <img src={article.cover_image} alt={article.title} className="w-full h-44 object-cover rounded-t-xl" loading="lazy" />
                    )}
                    <CardContent className="p-5">
                      <h2 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-[hsl(221,83%,22%)] transition-colors">{article.title}</h2>
                      {article.seo_description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{article.seo_description}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {new Date(article.created_at).toLocaleDateString('tr-TR')}
                        </div>
                        <span className="text-xs font-medium text-[hsl(221,83%,22%)] flex items-center gap-1">
                          Oku <ArrowRight className="h-3 w-3" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">Sayfa {page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
